import { useEffect, useMemo } from 'react'

import dayjs from 'dayjs'

import { ApplicantFilter } from '../types'
import {
  ANY_ASSIGNEE,
  ANY_ASSIGNEE_OPTION,
  TASK_DEADLINE_OPTIONS,
} from '../utils/taskFilterOptions'

import { useApplicantStagesFilter } from './useApplicantStagesFilter'
import { useCallCountFilter } from './useCallCountFilter'
import { useCustomFieldsFilter } from './useCustomFieldsFilter'
import { useEligibleForHireFilter } from './useEligibleForHireFilter'
import { useEndorsementsFilter } from './useEndorsementsFilter'
import { useEquipmentTypesFilter } from './useEquipmentTypesFilter'
import { useJobListingsFilter } from './useJobListingsFilter'
import { useLeadSourcesFilter } from './useLeadSourcesFilter'
import { useMissedCallCountFilter } from './useMissedCallCountFilter'
import { usePermanentlyDisqualifiedFilter } from './usePermanentlyDisqualifiedFilter'
import { useRecruitersFilter } from './useRecruitersFilter'
import { useStatesFilter } from './useStatesFilter'
import { useStepsFilter } from './useStepsFilter'
import { useTagsFilter } from './useTagsFilter'
import { useTaskTemplatesFilter } from './useTaskTemplatesFilter'
import { useYearsOfExperienceFilter } from './useYearsOfExperienceFilter'
import { useZipcodeFilter } from './useZipcodeFilter'

interface UseApplicantFiltersProps {
  selectedRecruiters: string[]
  selectedJobListings: string[]
  selectedLeadSources: string[]
  selectedTags: string[]
  selectedEndorsements: string[]
  selectedStates: string[]
  selectedEquipmentTypes: string[]
  selectedTaskDeadline: string[]
  selectedTaskDueDate: { gte?: Date | null; lte?: Date | null }
  selectedTaskTemplates: string[]
  selectedTaskAssignees: string[]
  selectedEligibleForHire: string[]
  selectedPermanentlyDisqualified: string[]
  selectedApplicantStages: string[]
  selectedSteps: string[]
  selectedYearsOfExperience: { gte?: number | null; lte?: number | null }
  selectedCallCount: { gte?: number | null; lte?: number | null }
  selectedMissedCallCount: { gte?: number | null; lte?: number | null }
  selectedZipcodes: string[]
  selectedApplicationDate: { gte?: Date | null; lte?: Date | null }
  selectedAvailableForWorkDate: { gte?: Date | null; lte?: Date | null }
  selectedHiredDate: { gte?: Date | null; lte?: Date | null }
  selectedTerminatedDate: { gte?: Date | null; lte?: Date | null }
  selectedDisqualifiedDate: { gte?: Date | null; lte?: Date | null }
  selectedNotInterestedDate: { gte?: Date | null; lte?: Date | null }
  selectedBookingDate: { gte?: Date | null; lte?: Date | null }
  selectedLastContactDate: { gte?: Date | null; lte?: Date | null }
  selectedCustomFieldValues?: any[]
  onRecruitersChange: (recruiters: string[]) => void
  onJobListingsChange: (jobListings: string[]) => void
  onLeadSourcesChange: (leadSources: string[]) => void
  onTagsChange: (tags: string[]) => void
  onEndorsementsChange: (endorsements: string[]) => void
  onStatesChange: (states: string[]) => void
  onEquipmentTypesChange: (equipmentTypes: string[]) => void
  onTaskDeadlineChange: (values: string[]) => void
  onTaskDueDateChange: (value: { gte?: Date | null; lte?: Date | null }) => void
  onTaskTemplatesChange: (templateIds: string[]) => void
  onTaskAssigneesChange: (ids: string[]) => void
  onEligibleForHireChange: (values: string[]) => void
  onPermanentlyDisqualifiedChange: (values: string[]) => void
  onApplicantStagesChange: (applicantStages: string[]) => void
  onStepsChange: (steps: string[]) => void
  onYearsOfExperienceChange: (value: {
    gte?: number | null
    lte?: number | null
  }) => void
  onCallCountChange: (value: {
    gte?: number | null
    lte?: number | null
  }) => void
  onMissedCallCountChange: (value: {
    gte?: number | null
    lte?: number | null
  }) => void
  onZipcodeChange: (zipcodes: string[]) => void
  onApplicationDateChange: (value: {
    gte?: Date | null
    lte?: Date | null
  }) => void
  onAvailableForWorkDateChange: (value: {
    gte?: Date | null
    lte?: Date | null
  }) => void
  onHiredDateChange: (value: { gte?: Date | null; lte?: Date | null }) => void
  onTerminatedDateChange: (value: {
    gte?: Date | null
    lte?: Date | null
  }) => void
  onDisqualifiedDateChange: (value: {
    gte?: Date | null
    lte?: Date | null
  }) => void
  onNotInterestedDateChange: (value: {
    gte?: Date | null
    lte?: Date | null
  }) => void
  onBookingDateChange: (value: { gte?: Date | null; lte?: Date | null }) => void
  onLastContactDateChange: (value: {
    gte?: Date | null
    lte?: Date | null
  }) => void
  onCustomFieldChange?: (customFieldId: string, value: any) => void
}

export const useApplicantFilters = ({
  selectedRecruiters,
  selectedJobListings,
  selectedLeadSources,
  selectedTags,
  selectedEndorsements,
  selectedStates,
  selectedEquipmentTypes,
  selectedEligibleForHire,
  selectedPermanentlyDisqualified,
  selectedApplicantStages,
  selectedSteps,
  selectedYearsOfExperience,
  selectedCallCount,
  selectedMissedCallCount,
  selectedZipcodes,
  selectedTaskDeadline,
  selectedTaskDueDate,
  selectedTaskTemplates,
  selectedTaskAssignees,
  selectedApplicationDate,
  selectedAvailableForWorkDate,
  selectedHiredDate,
  selectedTerminatedDate,
  selectedDisqualifiedDate,
  selectedNotInterestedDate,
  selectedBookingDate,
  selectedLastContactDate,
  selectedCustomFieldValues = [],
  onTaskDeadlineChange,
  onTaskDueDateChange,
  onTaskTemplatesChange,
  onTaskAssigneesChange,
  onRecruitersChange,
  onJobListingsChange,
  onLeadSourcesChange,
  onTagsChange,
  onEndorsementsChange,
  onStatesChange,
  onEquipmentTypesChange,
  onEligibleForHireChange,
  onPermanentlyDisqualifiedChange,
  onApplicantStagesChange,
  onStepsChange,
  onYearsOfExperienceChange,
  onCallCountChange,
  onMissedCallCountChange,
  onZipcodeChange,
  onApplicationDateChange,
  onAvailableForWorkDateChange,
  onHiredDateChange,
  onTerminatedDateChange,
  onDisqualifiedDateChange,
  onNotInterestedDateChange,
  onBookingDateChange,
  onLastContactDateChange,
  onCustomFieldChange,
}: UseApplicantFiltersProps) => {
  const recruitersFilter = useRecruitersFilter({
    selectedRecruiters,
    onRecruitersChange,
  })

  const jobListingsFilter = useJobListingsFilter({
    selectedJobListings,
    onJobListingsChange,
  })

  const leadSourcesFilter = useLeadSourcesFilter({
    selectedLeadSources,
    onLeadSourcesChange,
  })

  const tagsFilter = useTagsFilter({
    selectedTags,
    onTagsChange,
  })

  const endorsementsFilter = useEndorsementsFilter({
    selectedEndorsements,
    onEndorsementsChange,
  })

  const statesFilter = useStatesFilter({
    selectedStates,
    onStatesChange,
  })

  const equipmentTypesFilter = useEquipmentTypesFilter({
    selectedEquipmentTypes,
    onEquipmentTypesChange,
  })

  const eligibleForHireFilter = useEligibleForHireFilter({
    selectedEligibleForHire,
    onEligibleForHireChange,
  })

  const permanentlyDisqualifiedFilter = usePermanentlyDisqualifiedFilter({
    selectedPermanentlyDisqualified,
    onPermanentlyDisqualifiedChange,
  })

  const applicantStagesFilter = useApplicantStagesFilter({
    selectedApplicantStages,
    onApplicantStagesChange,
  })

  const stepsFilter = useStepsFilter({
    selectedSteps,
    onStepsChange,
  })

  const yearsOfExperienceFilter = useYearsOfExperienceFilter({
    selectedYearsOfExperience: {
      min: 0,
      max: 30,
      gte: selectedYearsOfExperience.gte,
      lte: selectedYearsOfExperience.lte,
    },
    onYearsOfExperienceChange,
  })

  const callCountFilter = useCallCountFilter({
    selectedCallCount: {
      min: 0,
      max: 20,
      gte: selectedCallCount.gte,
      lte: selectedCallCount.lte,
    },
    onCallCountChange,
  })

  const missedCallCountFilter = useMissedCallCountFilter({
    selectedMissedCallCount: {
      min: 0,
      max: 20,
      gte: selectedMissedCallCount.gte,
      lte: selectedMissedCallCount.lte,
    },
    onMissedCallCountChange,
  })

  const zipcodeFilter = useZipcodeFilter({
    selectedZipcodes,
    onZipcodeChange,
  })

  const customFieldsFilter = useCustomFieldsFilter()
  const taskTemplatesFilter = useTaskTemplatesFilter({
    selectedTaskTemplates,
    onTaskTemplatesChange,
  })
  const taskAssigneesFilter = useRecruitersFilter({
    selectedRecruiters: selectedTaskAssignees,
    onRecruitersChange: onTaskAssigneesChange,
  })

  // Selections restored from the URL or a saved view need their labels
  // before the submenu is ever opened
  useEffect(() => {
    if (selectedTaskAssignees.length) taskAssigneesFilter.loadData()
    if (selectedTaskTemplates.length) taskTemplatesFilter.loadData()
  }, [
    selectedTaskAssignees.length,
    selectedTaskTemplates.length,
    taskAssigneesFilter.loadData,
    taskTemplatesFilter.loadData,
  ])

  const filters = useMemo<ApplicantFilter[]>(() => {
    const availableForWorkMaxDate = dayjs()
      .add(3, 'month')
      .endOf('day')
      .toDate()

    return [
      {
        title: 'Task Deadline',
        selected: selectedTaskDeadline,
        onChange: onTaskDeadlineChange,
        isLoaded: true,
        urlKey: 'taskDeadline',
        alphabetize: false,
        options: TASK_DEADLINE_OPTIONS,
        dateRange: {
          value: selectedTaskDueDate,
          onChange: onTaskDueDateChange,
        },
      },
      {
        title: 'Task Template',
        selected: selectedTaskTemplates,
        onChange: taskTemplatesFilter.onChange,
        options: taskTemplatesFilter.options,
        isLoaded: taskTemplatesFilter.isLoaded,
        loadData: taskTemplatesFilter.loadData,
        urlKey: 'taskTemplates',
      },
      {
        title: 'Task Assignees',
        selected: taskAssigneesFilter.selected,
        onChange: taskAssigneesFilter.onChange,
        // "Any assignee" stays on top; employees follow alphabetically
        alphabetize: false,
        options: [
          {
            ...ANY_ASSIGNEE_OPTION,
            selected: taskAssigneesFilter.selected.includes(ANY_ASSIGNEE),
          },
          ...[...taskAssigneesFilter.options].sort((a, b) =>
            a.label.localeCompare(b.label)
          ),
        ],
        isLoaded: taskAssigneesFilter.isLoaded,
        loadData: taskAssigneesFilter.loadData,
        urlKey: 'taskAssignees',
      },
      {
        title: 'Employees',
        selected: recruitersFilter.selected,
        onChange: recruitersFilter.onChange,
        options: recruitersFilter.options,
        loadData: recruitersFilter.loadData,
        isLoaded: recruitersFilter.isLoaded,
        urlKey: 'recruiters',
      },
      {
        title: 'Position',
        selected: jobListingsFilter.selected,
        onChange: jobListingsFilter.onChange,
        options: jobListingsFilter.options,
        loadData: jobListingsFilter.loadData,
        isLoaded: jobListingsFilter.isLoaded,
        urlKey: 'jobListings',
        // Server-side search + paginated load-more so companies with arbitrary
        // catalog sizes can find and scroll through their listings (ENG-4248).
        // The hook debounces search and tracks pagination state; the menu skips
        // its client-side filterOptionsBySearch (options already reflect the
        // active search) and fires onLoadMore as the user scrolls.
        onSearchChange: jobListingsFilter.onSearchChange,
        isServerSideSearch: true,
        resetToInitial: jobListingsFilter.resetToInitial,
        onLoadMore: jobListingsFilter.loadMore,
        hasMore: jobListingsFilter.hasMore,
        loadingMore: jobListingsFilter.loadingMore,
      },
      {
        title: 'Lead Source',
        selected: leadSourcesFilter.selected,
        onChange: leadSourcesFilter.onChange,
        options: leadSourcesFilter.options,
        loadData: leadSourcesFilter.loadData,
        isLoaded: leadSourcesFilter.isLoaded,
        urlKey: 'leadSources',
        isNested: true,
      },
      {
        title: 'Tags',
        selected: tagsFilter.selected,
        onChange: tagsFilter.onChange,
        options: tagsFilter.options,
        loadData: tagsFilter.loadData,
        isLoaded: tagsFilter.isLoaded,
        urlKey: 'tags',
      },
      {
        title: 'Endorsements',
        selected: endorsementsFilter.selected,
        onChange: endorsementsFilter.onChange,
        options: endorsementsFilter.options,
        isLoaded: endorsementsFilter.isLoaded,
        urlKey: 'endorsements',
      },
      {
        title: 'States',
        selected: statesFilter.selected,
        onChange: statesFilter.onChange,
        options: statesFilter.options,
        loadData: statesFilter.loadData,
        isLoaded: statesFilter.isLoaded,
        urlKey: 'states',
      },
      {
        title: 'Equipment Types',
        selected: equipmentTypesFilter.selected,
        onChange: equipmentTypesFilter.onChange,
        options: equipmentTypesFilter.options,
        isLoaded: equipmentTypesFilter.isLoaded,
        urlKey: 'equipmentTypes',
      },
      {
        title: 'Eligible for Hire',
        selected: eligibleForHireFilter.selected,
        onChange: eligibleForHireFilter.onChange,
        options: eligibleForHireFilter.options,
        isLoaded: eligibleForHireFilter.isLoaded,
        urlKey: 'eligibleForHire',
      },
      {
        title: 'Permanently Disqualified',
        selected: permanentlyDisqualifiedFilter.selected,
        onChange: permanentlyDisqualifiedFilter.onChange,
        options: permanentlyDisqualifiedFilter.options,
        isLoaded: permanentlyDisqualifiedFilter.isLoaded,
        urlKey: 'permanentlyDisqualified',
      },
      {
        title: 'Applicant Stage',
        selected: applicantStagesFilter.selected,
        onChange: applicantStagesFilter.onChange,
        options: applicantStagesFilter.options,
        isLoaded: applicantStagesFilter.isLoaded,
        urlKey: 'applicantStages',
        isNested: true,
      },
      {
        title: 'Steps',
        selected: stepsFilter.selected,
        onChange: stepsFilter.onChange,
        options: stepsFilter.options,
        isLoaded: stepsFilter.isLoaded,
        urlKey: 'steps',
        isNested: true,
      },
      {
        title: 'Experience',
        selected: [],
        onChange: () => {},
        isLoaded: yearsOfExperienceFilter.isLoaded,
        urlKey: 'yearsOfExperience',
        isRange: true,
        rangeValue: yearsOfExperienceFilter.rangeValue,
        onRangeChange: yearsOfExperienceFilter.onChange,
      },
      {
        title: 'Call Count',
        selected: [],
        onChange: () => {},
        isLoaded: callCountFilter.isLoaded,
        urlKey: 'callCount',
        isRange: true,
        rangeValue: callCountFilter.rangeValue,
        onRangeChange: callCountFilter.onChange,
      },
      {
        title: 'Missed Call Count',
        selected: [],
        onChange: () => {},
        isLoaded: missedCallCountFilter.isLoaded,
        urlKey: 'missedOutboundCallCount',
        isRange: true,
        rangeValue: missedCallCountFilter.rangeValue,
        onRangeChange: missedCallCountFilter.onChange,
      },
      {
        title: 'Postal Codes',
        selected: zipcodeFilter.selected,
        onChange: zipcodeFilter.onChange,
        options: zipcodeFilter.options,
        isLoaded: zipcodeFilter.isLoaded,
        urlKey: 'zipCodes',
        isZipcode: true,
        newZipcode: zipcodeFilter.newZipcode,
        setNewZipcode: zipcodeFilter.setNewZipcode,
        handleAddZipcode: zipcodeFilter.handleAddZipcode,
        handleRemoveZipcode: zipcodeFilter.handleRemoveZipcode,
        suggestedZipcodes: zipcodeFilter.suggestedZipcodes,
        setSuggestedZipcodes: zipcodeFilter.setSuggestedZipcodes,
        handleAddSuggestedZipcode: zipcodeFilter.handleAddSuggestedZipcode,
        handleAddAllSuggestedZipcodes:
          zipcodeFilter.handleAddAllSuggestedZipcodes,
      },
      {
        title: 'Applied Date',
        selected: [],
        onChange: () => {},
        isLoaded: true,
        urlKey: 'applicationDate',
        isDate: true,
        dateValue: selectedApplicationDate,
        onDateChange: onApplicationDateChange,
      },
      {
        title: 'Last Contact Date',
        selected: [],
        onChange: () => {},
        isLoaded: true,
        urlKey: 'lastContactDate',
        isDate: true,
        dateValue: selectedLastContactDate,
        onDateChange: onLastContactDateChange,
      },
      {
        title: 'Available for Work Date',
        selected: [],
        onChange: () => {},
        isLoaded: true,
        urlKey: 'availableForWorkDate',
        isDate: true,
        dateValue: selectedAvailableForWorkDate,
        onDateChange: onAvailableForWorkDateChange,
        maxDate: availableForWorkMaxDate,
      },
      {
        title: 'Hired Date',
        selected: [],
        onChange: () => {},
        isLoaded: true,
        urlKey: 'hiredDate',
        isDate: true,
        dateValue: selectedHiredDate,
        onDateChange: onHiredDateChange,
      },
      {
        title: 'Terminated Date',
        selected: [],
        onChange: () => {},
        isLoaded: true,
        urlKey: 'terminatedDate',
        isDate: true,
        dateValue: selectedTerminatedDate,
        onDateChange: onTerminatedDateChange,
      },
      {
        title: 'Disqualified Date',
        selected: [],
        onChange: () => {},
        isLoaded: true,
        urlKey: 'disqualifiedDate',
        isDate: true,
        dateValue: selectedDisqualifiedDate,
        onDateChange: onDisqualifiedDateChange,
      },
      {
        title: 'Not Interested Date',
        selected: [],
        onChange: () => {},
        isLoaded: true,
        urlKey: 'notInterestedDate',
        isDate: true,
        dateValue: selectedNotInterestedDate,
        onDateChange: onNotInterestedDateChange,
      },
      {
        title: 'Booking Date',
        selected: [],
        onChange: () => {},
        isLoaded: true,
        urlKey: 'bookingDate',
        isDate: true,
        dateValue: selectedBookingDate,
        onDateChange: onBookingDateChange,
        allowFutureDates: true,
      },
      // Individual custom field filters
      ...customFieldsFilter.searchableFields
        .map((field) => {
          const currentValue = customFieldsFilter.selectedValues.find(
            (v) => v.customFieldId === field.customFieldId
          )

          // Select fields → standard checkbox multi-select filter
          if (
            field.fieldType === 'SINGLE_SELECT' ||
            field.fieldType === 'MULTI_SELECT'
          ) {
            return {
              title: field.label,
              selected: currentValue?.selectValues || [],
              onChange: (values: string[]) => {
                customFieldsFilter.handleChange(
                  field.customFieldId,
                  values.length > 0 ? values : null
                )
              },
              options: (field.options || []).map((opt) => ({
                label: opt.label,
                value: opt.value,
              })),
              isLoaded: true,
              urlKey: `customField_${field.customFieldId}`,
            }
          }

          // Date fields → date filter with prepared date ranges
          if (field.fieldType === 'DATE') {
            return {
              title: field.label,
              selected: [],
              onChange: () => {},
              isLoaded: true,
              urlKey: `customField_${field.customFieldId}`,
              isDate: true,
              dateValue: currentValue?.dateValue || { gte: null, lte: null },
              onDateChange: (value) => {
                customFieldsFilter.handleChange(
                  field.customFieldId,
                  value.gte || value.lte ? value : null
                )
              },
              allowFutureDates: true,
            }
          }

          // Boolean fields → standard checkbox filter with Yes/No
          if (field.fieldType === 'BOOLEAN') {
            const selected =
              currentValue?.booleanValue !== undefined
                ? [String(currentValue.booleanValue)]
                : []
            return {
              title: field.label,
              selected,
              onChange: (values: string[]) => {
                customFieldsFilter.handleChange(
                  field.customFieldId,
                  values.length > 0
                    ? values[values.length - 1] === 'true'
                    : null
                )
              },
              options: [
                { label: 'Yes', value: 'true' },
                { label: 'No', value: 'false' },
              ],
              isLoaded: true,
              urlKey: `customField_${field.customFieldId}`,
            }
          }

          // Fallback for unsupported field types (STRING can't be searchable)
          return null
        })
        .filter(Boolean),
    ]
  }, [
    recruitersFilter,
    jobListingsFilter,
    leadSourcesFilter,
    tagsFilter,
    endorsementsFilter,
    statesFilter,
    equipmentTypesFilter,
    eligibleForHireFilter,
    permanentlyDisqualifiedFilter,
    applicantStagesFilter,
    stepsFilter,
    yearsOfExperienceFilter,
    callCountFilter,
    missedCallCountFilter,
    zipcodeFilter,
    customFieldsFilter,
    selectedTaskDeadline,
    onTaskDeadlineChange,
    selectedTaskDueDate,
    onTaskDueDateChange,
    selectedTaskTemplates,
    taskTemplatesFilter,
    taskAssigneesFilter,
    selectedApplicationDate,
    selectedAvailableForWorkDate,
    selectedHiredDate,
    selectedTerminatedDate,
    selectedDisqualifiedDate,
    selectedNotInterestedDate,
    selectedBookingDate,
    selectedLastContactDate,
    onApplicationDateChange,
    onAvailableForWorkDateChange,
    onHiredDateChange,
    onTerminatedDateChange,
    onDisqualifiedDateChange,
    onNotInterestedDateChange,
    onBookingDateChange,
    onLastContactDateChange,
  ])

  return {
    filters,
    loading:
      recruitersFilter.loading ||
      jobListingsFilter.loading ||
      tagsFilter.loading,
  }
}
